import { lazy, Suspense, type ReactNode } from "react";
import { useT } from "../i18n/catalogue.js";

import { EmptyState, LoadingState } from "../states/index.js";
import { isEmptyFunnel, type FunnelInput } from "./funnel.js";

/**
 * An income funnel: what came in, each category taken out in turn, and what is left.
 *
 * ==========================================================================================
 * A DESIGN-SYSTEM ADDITION, AND IT OVERTURNS A REFUSAL THAT STILL STANDS
 * ==========================================================================================
 *
 * `dashboard.md` refuses "a funnel for In → Out → Left" and is right to: `Out` is not a stage of `In`, so a
 * three-band funnel asserts a narrowing that is not there. THIS IS A DIFFERENT SHAPE. Its stages are the
 * expense categories, each a genuine subset of the money still unspent above it, and its arithmetic closes.
 * `funnel.ts` carries the distinction and the closure argument, which is the part a future change is most
 * likely to break.
 *
 * `lazy` at MODULE SCOPE, as the other charts are.
 */
const Impl = lazy(() =>
  import("./FunnelChartImpl.js").then((m) => ({ default: m.FunnelChartImpl })),
);

export interface FunnelChartProps {
  /** Income, the whole of what went out, and the categories to draw. See `FunnelInput`. */
  input: FunnelInput;
  /** Shown when there is nothing to draw. The caller knows what "nothing" means here. */
  emptyMessage?: ReactNode;
}

export function FunnelChart({ input, emptyMessage }: FunnelChartProps): ReactNode {
  const t = useT();
  // Checked BEFORE the boundary, so a household with nothing recorded never downloads Recharts.
  if (isEmptyFunnel(input)) return <EmptyState>{emptyMessage ?? t("ui.chart.empty")}</EmptyState>;

  return (
    <Suspense fallback={<LoadingState>{t("ui.chart.loading")}</LoadingState>}>
      <Impl input={input} />
    </Suspense>
  );
}
